import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Box, Flex, Text, VStack } from "@chakra-ui/react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  GenerateReportWizardStepper,
  GenerateReportWizardStepperContent,
} from "@/views/Dashboard/Reports/GenerateReportWizardStepper";
import type { BrokerCapabilities } from "../../../services/brokerIntegrationApi";
import type { CustomCsvImportCapabilities } from "../../../services/brokerIntegrationApi";
import type {
  CustomCsvFormFieldsInput,
  CustomCsvImportResponse,
  CustomCsvWizardState,
} from "../../../types/customCsvImport";
import type { TradeAccountOption } from "../../../contexts/ActiveBrokerAccountContext";
import {
  buildAcknowledgedWarningCodes,
  buildCustomCsvDefaults,
  buildCustomCsvImportPayload,
  createInitialWizardState,
  getMappableFieldsForMode,
  resolveCustomCsvCapability,
  validateColumnMappingForShape,
  validateDatetimeFormat,
} from "../../../utils/customCsvImport";
import { findLayoutShape } from "../../../utils/layoutShapes";
import {
  buildCustomCsvFormData,
  fetchCustomCsvTemplates,
  postCustomCsvImport,
  postCustomCsvPreview,
} from "../../../services/customCsvImportApi";
import { useRateLimitCooldown } from "../../../hooks/useRateLimitCooldown";
import {
  CUSTOM_CSV_WIZARD_STEPS,
  type CustomCsvWizardStepId,
  visibleWizardSteps,
} from "./customCsvImportConstants";
import CustomCsvLayoutPickStep, {
  type CustomCsvLayoutPickStepHandle,
} from "./CustomCsvLayoutPickStep";
import CustomCsvUploadStep from "./CustomCsvUploadStep";
import CustomCsvColumnMappingStep from "./CustomCsvColumnMappingStep";
import CustomCsvValueTransformsStep from "./CustomCsvValueTransformsStep";
import CustomCsvMetadataStep from "./CustomCsvMetadataStep";
import CustomCsvPreviewStep from "./CustomCsvPreviewStep";
import CustomCsvWarningAckStep from "./CustomCsvWarningAckStep";
import CustomCsvImportStep from "./CustomCsvImportStep";
import type { CsvImportProfile } from "../../../types/customCsvImport";

export type CustomCsvWizardNavigationHandle = {
  tryHandleBack: () => boolean;
};

export type CustomCsvWizardFooterAction = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  isLoading?: boolean;
  loadingLabel?: string;
};

export interface CustomCsvImportWizardProps {
  capability: BrokerCapabilities;
  allCapabilities?: BrokerCapabilities[];
  brokerAccounts: TradeAccountOption[];
  initialAccountName?: string;
  initialProfileSlug?: string;
  onImportComplete: (result: CustomCsvImportResponse) => void;
  /**
   * Demo Entry Analysis: same wizard UI; demo inspect/preview endpoints; cohort handoff
   * via onDemoReady — never POST custom-csv/import.
   */
  mode?: "platform" | "demo";
  onDemoReady?: (payload: CustomCsvFormFieldsInput) => void | Promise<void>;
  /** When the wizard cannot go back further (e.g. demo broker picker). */
  onExitToParent?: () => void;
  embeddedInConnectWizard?: boolean;
  navigationRef?: React.RefObject<CustomCsvWizardNavigationHandle | null>;
  onEmbeddedFooterChange?: (action: CustomCsvWizardFooterAction | null) => void;
}

function stepIdAtIndex(
  steps: Array<(typeof CUSTOM_CSV_WIZARD_STEPS)[number]>,
  index: number
): CustomCsvWizardStepId {
  return steps[index]?.id ?? "start";
}

const STEP_LABEL_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal text-[color:var(--brand-tropical-surf)]";

const CustomCsvImportWizard = ({
  capability,
  brokerAccounts,
  initialAccountName = "",
  initialProfileSlug,
  onImportComplete,
  mode = "platform",
  onDemoReady,
  onExitToParent,
  embeddedInConnectWizard = false,
  navigationRef,
  onEmbeddedFooterChange,
}: CustomCsvImportWizardProps): React.ReactElement => {
  const demoMode = mode === "demo";
  const resolvedCsv = resolveCustomCsvCapability(capability) as CustomCsvImportCapabilities;
  const csvImport: CustomCsvImportCapabilities = demoMode
    ? {
        ...resolvedCsv,
        supported: true,
        inspect_endpoint:
          resolvedCsv?.inspect_endpoint || "/demo/entry-analysis/inspect",
        preview_endpoint:
          resolvedCsv?.preview_endpoint || "/demo/entry-analysis/preview",
        // Cohort is the demo “import” target — never production custom-csv/import.
        endpoint: resolvedCsv?.endpoint || "/demo/entry-analysis/cohort",
      }
    : resolvedCsv;
  const previewRateLimit = useRateLimitCooldown("custom-csv-preview");
  const importRateLimit = useRateLimitCooldown("csv-import");
  const layoutPickRef = useRef<CustomCsvLayoutPickStepHandle | null>(null);
  const [layoutNavRevision, setLayoutNavRevision] = useState(0);

  const [state, setState] = useState<CustomCsvWizardState>(() =>
    createInitialWizardState({
      accountName: demoMode ? "demo" : initialAccountName,
      profileSlug: initialProfileSlug,
      startSource: initialProfileSlug ? "profile" : "layout",
      ...(demoMode
        ? {
            displayName: "Demo",
            timeZone:
              Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
          }
        : {}),
    })
  );
  const [stepIndex, setStepIndex] = useState(0);
  const [detectedHeaders, setDetectedHeaders] = useState<string[]>([]);
  const [templates, setTemplates] = useState<CsvImportProfile[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<CustomCsvImportResponse | null>(null);

  const steps = useMemo(
    () => visibleWizardSteps(state.preview?.warnings.length ?? 0),
    [state.preview?.warnings.length]
  );
  const currentStepId = stepIdAtIndex(steps, stepIndex);

  const patchState = useCallback((patch: Partial<CustomCsvWizardState>) => {
    setState((prev) => ({ ...prev, ...patch }));
  }, []);

  const bumpLayoutNav = useCallback(() => {
    setLayoutNavRevision((revision) => revision + 1);
  }, []);

  useEffect(() => {
    if (demoMode) {
      setTemplates([]);
      return;
    }
    fetchCustomCsvTemplates(csvImport)
      .then((res) => setTemplates(res.templates ?? []))
      .catch(() => setTemplates([]));
  }, [csvImport, demoMode]);

  useEffect(() => {
    if (!initialProfileSlug || !templates.length) return;
    const template = templates.find((p) => p.profile_slug === initialProfileSlug);
    if (!template) return;
    setState((prev) => ({
      ...prev,
      startSource: "profile",
      profileSlug: template.profile_slug,
      layoutShapeId: template.layout_shape_id ?? undefined,
      importMode: template.import_mode,
      columnMapping: { ...template.column_mapping },
      valueTransforms: { ...template.value_transforms },
      defaults: { ...(template.defaults ?? {}) },
      displayName: template.display_name,
      quantityUnit: template.defaults?.quantity_unit ?? "units",
      importInstrument: template.defaults?.import_instrument ?? "",
      statementAccountCurrency: String(template.defaults?.statement_account_currency ?? ""),
      contractMultipliers: { ...(template.defaults?.contract_multipliers ?? {}) },
      timeZone: template.defaults?.time_zone ?? prev.timeZone,
    }));
    setStepIndex(1);
  }, [initialProfileSlug, templates]);

  useEffect(() => {
    if (currentStepId === "start") {
      layoutPickRef.current?.syncViewFromState();
    }
  }, [currentStepId]);

  useEffect(() => {
    setStepIndex((index) => Math.min(index, Math.max(steps.length - 1, 0)));
  }, [steps.length]);

  const advanceStep = () => {
    setStepIndex((index) => Math.min(index + 1, steps.length - 1));
  };

  const goNext = async () => {
    if (currentStepId === "start") {
      if (layoutPickRef.current?.handleContinue()) {
        return;
      }
    }

    const nextId = stepIdAtIndex(steps, stepIndex + 1);
    if (nextId === "preview" && state.csvFile) {
      await runPreview();
    }
    advanceStep();
  };

  const goBack = () => {
    if (currentStepId === "start" && layoutPickRef.current?.handleBack()) {
      return;
    }
    if (stepIndex === 0) {
      onExitToParent?.();
      return;
    }
    setStepIndex((index) => Math.max(index - 1, 0));
  };

  const buildFormInput = useCallback(() => {
    if (!state.csvFile) throw new Error("CSV file is required");
    const { columnMapping, valueTransforms } = buildCustomCsvImportPayload({
      columnMapping: state.columnMapping,
      valueTransforms: state.valueTransforms,
      layoutShapeId: state.layoutShapeId,
    });
    return {
      file: state.csvFile,
      timeZone: state.timeZone,
      importMode: state.importMode,
      columnMapping,
      valueTransforms,
      defaults: buildCustomCsvDefaults(state),
      layoutShapeId: state.layoutShapeId,
      profileSlug: state.profileSlug,
      accountName: state.accountName,
      importInstrument: state.importInstrument,
      headerRowIndex: state.headerRowIndex,
    };
  }, [state]);

  const runPreview = async () => {
    if (previewRateLimit.isCoolingDown) {
      setPreviewError(`Try again in ${previewRateLimit.retryAfterSeconds}s.`);
      return;
    }
    const mappable = getMappableFieldsForMode(csvImport, state.importMode);
    const layoutShape = findLayoutShape(csvImport, state.layoutShapeId);
    const mappingErrors = validateColumnMappingForShape(
      mappable,
      state.columnMapping,
      layoutShape,
      state.importInstrument
    );
    if (mappingErrors.length) {
      setPreviewError(mappingErrors.join(" "));
      return;
    }
    if (!state.csvFile) {
      setPreviewError("Upload a CSV file first.");
      return;
    }
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const form = buildCustomCsvFormData(buildFormInput());
      if (demoMode) {
        form.append("broker_name", "custom_csv");
      }
      const preview = await postCustomCsvPreview(form, csvImport.preview_endpoint);
      patchState({
        preview: {
          ...preview,
          // Demo preview has no import token — keep shape for shared UI.
          preview_token: preview.preview_token || "",
        },
        acknowledgedWarningCodes: new Set(),
        importMode: (preview.import_mode as typeof state.importMode) || state.importMode,
      });
      // Keep mapper columns from inspect header row — do not replace with preview.headers
      // (preview can mis-label cells and pollute the chip list with values like "0.00").
    } catch (e: unknown) {
      setPreviewError(e instanceof Error ? e.message : "Preview failed");
    } finally {
      setPreviewLoading(false);
    }
  };

  const runImport = async () => {
    if (demoMode) {
      if (!onDemoReady) {
        setImportError("Demo handoff is not configured.");
        return;
      }
      if (!state.preview?.can_import) {
        setImportError("Run a successful preview first.");
        return;
      }
      const warnings = state.preview.warnings ?? [];
      const acked = buildAcknowledgedWarningCodes(warnings, state.acknowledgedWarningCodes);
      if (warnings.length && acked.split(",").filter(Boolean).length < warnings.length) {
        setImportError("Acknowledge all warnings before continuing.");
        return;
      }
      setImportLoading(true);
      setImportError(null);
      try {
        await onDemoReady(buildFormInput());
      } catch (e: unknown) {
        setImportError(e instanceof Error ? e.message : "Could not continue with this CSV.");
      } finally {
        setImportLoading(false);
      }
      return;
    }

    if (importRateLimit.isCoolingDown) {
      setImportError(`Try again in ${importRateLimit.retryAfterSeconds}s.`);
      return;
    }
    if (!state.preview?.preview_token) {
      setImportError("Run preview first.");
      return;
    }
    const warnings = state.preview.warnings ?? [];
    const acked = buildAcknowledgedWarningCodes(warnings, state.acknowledgedWarningCodes);
    if (warnings.length && acked.split(",").filter(Boolean).length < warnings.length) {
      setImportError("Acknowledge all warnings before importing.");
      return;
    }
    setImportLoading(true);
    setImportError(null);
    try {
      const form = buildCustomCsvFormData(
        {
          ...buildFormInput(),
          previewToken: state.preview.preview_token,
          displayName: state.displayName,
          acknowledgedWarningCodes: acked ? acked.split(",") : [],
          saveProfile: state.saveProfile,
          saveTemplate: state.saveTemplate || state.saveProfile,
        },
        { forImport: true }
      );
      const result = await postCustomCsvImport(form, csvImport.endpoint);
      setImportResult(result);
      onImportComplete(result);
    } catch (e: unknown) {
      setImportError(e instanceof Error ? e.message : "Import failed");
    } finally {
      setImportLoading(false);
    }
  };

  const canContinue = (): boolean => {
    switch (currentStepId) {
      case "start":
        return layoutPickRef.current?.canContinue() ?? false;
      case "upload":
        return Boolean(state.csvFile) && detectedHeaders.length > 0;
      case "map": {
        const mappable = getMappableFieldsForMode(csvImport, state.importMode);
        const layoutShape = findLayoutShape(csvImport, state.layoutShapeId);
        return (
          validateColumnMappingForShape(
            mappable,
            state.columnMapping,
            layoutShape,
            state.importInstrument
          ).length === 0
        );
      }
      case "formats": {
        const datetimeFormat = String(state.valueTransforms.datetime_format ?? "").trim();
        return Boolean(datetimeFormat) && !validateDatetimeFormat(datetimeFormat);
      }
      case "metadata":
        if (demoMode) {
          return Boolean(state.timeZone.trim());
        }
        return (
          Boolean(state.displayName.trim()) &&
          Boolean(state.accountName.trim()) &&
          Boolean(state.timeZone.trim())
        );
      case "preview":
        return Boolean(state.preview?.can_import);
      case "acknowledge": {
        const warnings = state.preview?.warnings ?? [];
        return warnings.every((w) => state.acknowledgedWarningCodes.has(w.code));
      }
      case "import":
        return demoMode ? Boolean(state.preview?.can_import) : Boolean(importResult);
      default:
        return false;
    }
  };

  const backDisabled = useMemo(() => {
    if (stepIndex > 0) return false;
    if (
      currentStepId === "start" &&
      layoutPickRef.current &&
      !layoutPickRef.current.isAtFirstView()
    ) {
      return false;
    }
    if (onExitToParent) return false;
    return true;
  }, [stepIndex, currentStepId, layoutNavRevision, onExitToParent]);

  const renderStepContent = (stepId: CustomCsvWizardStepId) => {
    switch (stepId) {
      case "start":
        return (
          <CustomCsvLayoutPickStep
            ref={layoutPickRef}
            csvImport={csvImport}
            state={state}
            templates={templates}
            onStateChange={patchState}
            onSkipToUpload={advanceStep}
            onViewChange={bumpLayoutNav}
          />
        );
      case "upload":
        return (
          <CustomCsvUploadStep
            csvImport={csvImport}
            state={state}
            onStateChange={patchState}
            detectedHeaders={detectedHeaders}
            onDetectedHeadersChange={setDetectedHeaders}
          />
        );
      case "map":
        return (
          <CustomCsvColumnMappingStep
            csvImport={csvImport}
            state={state}
            fileHeaders={detectedHeaders}
            onStateChange={patchState}
          />
        );
      case "formats":
        return (
          <CustomCsvValueTransformsStep
            csvImport={csvImport}
            state={state}
            onStateChange={patchState}
          />
        );
      case "metadata":
        return (
          <CustomCsvMetadataStep
            csvImport={csvImport}
            state={state}
            brokerAccounts={brokerAccounts}
            onStateChange={patchState}
            demoMode={demoMode}
          />
        );
      case "preview":
        return (
          <CustomCsvPreviewStep
            state={state}
            fileHeaders={detectedHeaders}
            isLoading={previewLoading}
            error={previewError}
            onRunPreview={runPreview}
          />
        );
      case "acknowledge":
        return <CustomCsvWarningAckStep state={state} onStateChange={patchState} />;
      case "import":
        return (
          <CustomCsvImportStep
            state={state}
            isLoading={importLoading}
            error={importError}
            result={demoMode ? null : importResult}
            onStateChange={patchState}
            onImport={runImport}
            hideImportButton
            demoMode={demoMode}
          />
        );
      default:
        return null;
    }
  };

  const continueLabel =
    currentStepId === "import" ? (demoMode ? "Continue to analysis" : "Import trades") : "Continue";

  const primaryDisabled = (() => {
    if (currentStepId === "import") {
      if (demoMode) {
        return importLoading || !state.preview?.can_import;
      }
      return (
        importLoading ||
        Boolean(importResult) ||
        !state.preview?.can_import ||
        !state.preview?.preview_token
      );
    }
    return !canContinue() || previewLoading;
  })();

  const handlePrimary = () => {
    if (currentStepId === "import") {
      void runImport();
      return;
    }
    void goNext();
  };

  const handlePrimaryRef = useRef(handlePrimary);
  handlePrimaryRef.current = handlePrimary;

  const primaryLoading =
    importLoading || (previewLoading && currentStepId === "metadata");

  const showInternalFooter =
    !embeddedInConnectWizard &&
    !demoMode &&
    (currentStepId !== "import" || !importResult);

  const liftFooterToParent = Boolean(onEmbeddedFooterChange) && (embeddedInConnectWizard || demoMode);

  useImperativeHandle(
    navigationRef,
    () => ({
      tryHandleBack(): boolean {
        if (stepIndex > 0) {
          goBack();
          return true;
        }
        if (currentStepId === "start" && layoutPickRef.current?.handleBack()) {
          return true;
        }
        if (onExitToParent) {
          onExitToParent();
          return true;
        }
        return false;
      },
    }),
    [stepIndex, currentStepId, layoutNavRevision, onExitToParent]
  );

  useEffect(() => {
    if (!liftFooterToParent || !onEmbeddedFooterChange) return;

    if (currentStepId === "import" && importResult) {
      onEmbeddedFooterChange(null);
      return;
    }

    onEmbeddedFooterChange({
      label: continueLabel,
      disabled: primaryDisabled,
      isLoading: primaryLoading,
      loadingLabel:
        currentStepId === "import"
          ? demoMode
            ? "Continuing…"
            : "Importing…"
          : "Running preview…",
      onClick: () => {
        void handlePrimaryRef.current();
      },
    });
  }, [
    liftFooterToParent,
    onEmbeddedFooterChange,
    continueLabel,
    primaryDisabled,
    primaryLoading,
    currentStepId,
    importResult,
  ]);

  useEffect(() => {
    if (!liftFooterToParent || !onEmbeddedFooterChange) return;
    return () => {
      onEmbeddedFooterChange(null);
    };
  }, [liftFooterToParent, onEmbeddedFooterChange]);

  return (
    <VStack align="stretch" spacing={0} data-custom-csv-import-wizard="" pt={4}>
      <GenerateReportWizardStepper
        activeStepIndex={stepIndex}
        stepCount={steps.length}
        onActiveStepIndexChange={setStepIndex}
      >
        {steps.map((step, index) => (
          <GenerateReportWizardStepperContent key={step.id} step={index + 1}>
            <Text className={STEP_LABEL_CLASS} m={0} lineHeight="1.25rem">
              {step.label}
            </Text>
            <Box mt={1}>{renderStepContent(step.id)}</Box>
          </GenerateReportWizardStepperContent>
        ))}
      </GenerateReportWizardStepper>

      {showInternalFooter ? (
        <Flex justify="space-between" align="center" gap={3} flexWrap="wrap" pt={6}>
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-font="primary"
            data-reports-rename-button=""
            className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
            onClick={goBack}
            disabled={backDisabled}
          >
            <span data-font="primary" className="truncate text-gray-500 normal-case tracking-normal">
              Back
            </span>
          </Button>
          <Button
            type="button"
            variant="gradient"
            size="sm"
            data-font="primary"
            className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
            onClick={handlePrimary}
            disabled={primaryDisabled}
          >
            {primaryLoading ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden />
                {currentStepId === "import"
                  ? demoMode
                    ? "Continuing…"
                    : "Importing…"
                  : "Running preview…"}
              </>
            ) : (
              continueLabel
            )}
          </Button>
        </Flex>
      ) : null}
    </VStack>
  );
};

export default CustomCsvImportWizard;
